import type { LifecycleStateLabel } from "@/api/projects";
import { ACTIVE_RUN_STATES } from "@/api/runs";

/**
 * **What a missing run timestamp is allowed to look like on screen
 * (flowstack#1270).**
 *
 * The server now sends `started_at` / `finished_at` / `duration_seconds` for
 * real, off VCM's own record. It still sends `null` for four populations that
 * are not going away: rows written before the columns existed (there is no
 * backfill — nothing was ever captured to backfill from), runs FlowStack
 * terminalised itself rather than via a VCM completion, stub-mode runs, and
 * runs still in flight.
 *
 * `null` therefore means **not recorded**, and it is never a fact about the
 * run. That is not a hypothetical: a cache-hit investigation on this project
 * read `started_at: null` as "served from cache" across 300 rows of which
 * *zero* were populated, so the field said nothing about caching and every run
 * looked like a hit. The absence was uniform, which is exactly what made it
 * convincing.
 *
 * A bare em dash is the shape that invites that reading a second time: in a
 * Duration column it is indistinguishable from a run that took no time, and it
 * tells the reader nothing about why the cell is empty. So a terminal run with
 * no window says so in words. An IN-FLIGHT run keeps the dash — there
 * genuinely is no duration yet, "not recorded" would be wrong, and the state
 * badge beside it already says the run is going.
 */
export const NOT_RECORDED_LABEL = "not recorded";

/** The dash an in-flight run's empty timing cell keeps. Matches
 * `formatDuration`'s own null rendering, so the two agree by construction. */
export const NOT_YET_LABEL = "—";

/**
 * What a cache hit's empty timing says (flowstack#1943). A run served from
 * the cache never starts, so it has no compute window BY CONSTRUCTION — nothing
 * was lost, and "not recorded" read as if it had been.
 *
 * This is keyed on the server's `cache_hit` (the durable `cached` transition),
 * never on the null itself: the reading the comment above warns against is
 * inferring a cache hit FROM a missing `started_at`, and that stays wrong.
 */
export const SERVED_FROM_CACHE_LABEL = "served from cache";

/**
 * The label for a timing field the API returned as `null`, given the run's
 * lifecycle state and whether the server says it was served from the cache.
 *
 * Takes the STATE rather than a boolean so the two readings can never drift
 * apart at a call site, and reads the active set from `api/runs.ts` rather
 * than restating the seven labels — the same import `RunHeader` makes.
 */
export function absentTimingLabel(status: LifecycleStateLabel, cacheHit = false): string {
  if (ACTIVE_RUN_STATES.has(status)) return NOT_YET_LABEL;
  return cacheHit ? SERVED_FROM_CACHE_LABEL : NOT_RECORDED_LABEL;
}
